import axios, { AxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';
import merge from 'deepmerge';
import apiConfig from './apiConfig';
import { AuthProtect, BaseURLSelect } from '@/common/enum';
import type EndPointTypes from 'interfaces/EndPointTypes.ts';
import { store } from '@/store';
import { endSession } from '@/utilities/logout.ts';
import { ensureFreshSession, isSessionUnrecoverable } from '@/store/authToken/authToken.session.ts';
import { selectConnectionHealth } from 'store/connection/connection.selector.ts';
import { Nullable } from 'interfaces/Nullable.ts';

export const genericErrorMessage =
  'We just experienced a technical issue, please wait a few seconds and try again.';

/**
 * Fixed string with no interpolated request detail, so a missing credential is
 * diagnosable from the console without the token itself ever being logged.
 */
export const missingJwtWarning =
  'apiFetch: authToken.jwtToken is empty, sending no Authorization header. ' +
  'The damage assessment API rejects every authenticated request until the ' +
  'session carries a Cognito ID token again.';

/**
 * Shown when a request failed because the session cannot continue -- the
 * gateway rejected the token outright, or the identity middleware accepted it
 * but could not resolve a DMA account from its claims. Both read the same to
 * the user: sign in again, nothing queued is lost. The identity middleware's
 * own `remediation` field names the credential mechanism for developers and
 * never reaches this message.
 */
export const reauthRequiredMessage =
  'Your session has expired. Your saved assessments are safe -- sign in again to sync them.';

/**
 * Thrown once `endSession()` has already run, so a caller can show
 * `reauthRequiredMessage` instead of the generic technical-issue text
 * `toClientError` produces for everything else.
 */
export class AuthFailureError extends Error {
  constructor(message: string = reauthRequiredMessage) {
    super(message);
    this.name = 'AuthFailureError';
  }
}

/**
 * The two identity-middleware codes that mean the session is dead: the
 * gateway already accepted the token, but its claims do not resolve to an
 * account, and retrying cannot fix a claim that will not decode. The
 * middleware's third code, `auth.identity.nobody` (a write that reached it
 * with no parsed body), is a client bug rather than a session problem -- it
 * is still recognized and logged below, just not listed here, so it falls
 * through to the existing generic disposition instead of re-auth.
 */
const AUTH_IDENTITY_MISSING = 'auth.identity.missing';
const AUTH_IDENTITY_MALFORMED = 'auth.identity.malformed';

/**
 * Reads the identity middleware's failure code out of a response body. A body
 * that is not this shape -- the gateway's own `{"message":"Unauthorized"}`, or
 * anything unparseable -- yields null and is left to the existing generic path.
 */
function readIdentityFailureCode(data: unknown): Nullable<string> {
  if (!data || typeof data !== 'object') return null;
  const { code } = data as Record<string, unknown>;
  return typeof code === 'string' ? code : null;
}

const redactedValue = '[redacted]';

/**
 * Transport-level backstop so a hung backend fails the request instead of
 * leaving the caller on a spinner until the browser gives up. No caller here
 * arms its AbortController with a deadline, and the one long deadline in the app
 * (`PARCEL_DOWNLOAD_TIMEOUT_MS` in `hooks/useParcelData.ts`) is on a direct axios
 * call that never reaches this module -- so this cuts nothing a caller owns. A
 * caller that adds its own deadline should keep it below this value, so its own
 * timeout fires first and its own message survives.
 */
export const REQUEST_TIMEOUT_MS = 30000;

const palmettoBaseURL = import.meta.env.VITE_PALMETTO_ENDPOINT;
const dmaBaseURL = import.meta.env.VITE_DAMAGEASSESSMENT_API_ENDPOINT;

/**
 * Copy of `source` with each named key's value replaced by a placeholder.
 * Matching is case-insensitive because axios normalizes header casing, so
 * `Authorization` and `authorization` both have to be caught.
 */
function redactKeys(source: unknown, keys: string[]): unknown {
  if (!source || typeof source !== 'object') return source;
  const targets = keys.map((key) => key.toLowerCase());
  return Object.fromEntries(
    Object.entries(source as Record<string, unknown>).map(([key, value]) => [
      key,
      targets.includes(key.toLowerCase()) ? redactedValue : value,
    ])
  );
}

/**
 * A log-safe view of a failed request. A bearer credential must never reach the
 * console, so both places one can sit -- the `Authorization` header and the
 * `access_token` parameter -- are replaced with a placeholder, while method,
 * url, status and response body stay readable so the failure is diagnosable.
 * A non-axios throw carries no request, so it is passed through as before.
 */
export function redactCredentials(error: unknown): unknown {
  if (!isAxiosError(error)) return error instanceof Error ? error.message : error;
  const { message, code, config, response } = error;
  return {
    message,
    code,
    status: response?.status,
    data: response?.data,
    method: config?.method,
    url: config?.url,
    params: redactKeys(config?.params, ['access_token']),
    headers: redactKeys(config?.headers, ['authorization']),
  };
}

/**
 * Request lifecycle for an authenticated call:
 *
 *   ensureFreshSession ──▶ send ──┬─ 2xx ──────────────────────────────▶ data
 *                                 ├─ 401, identity.missing/malformed ─▶ online? ─┬─ no  ─▶ kept
 *                                 │                                              └─ yes ─▶ end session
 *                                 ├─ 401, other body ─▶ renewable? ──┬─ yes ─▶ retry once
 *                                 │                                  └─ no  ─▶ end session
 *                                 └─ identity.nobody, or anything else ─▶ generic error
 *
 * Two APIs, two credentials:
 *
 *   DMA      ──▶ Authorization: <Cognito ID token, raw>   (no access_token param)
 *   Palmetto ──▶ Authorization: <opaque access token>
 *                access_token=<opaque access token>
 *
 * The Palmetto API is LoopBack, which answers 401 for both "no valid session"
 * and "this user may not touch this resource". Only the first is a reason to
 * end the session -- ending it on an authorization denial throws away the
 * user's whole session, their offline queue and their persisted state over a
 * single forbidden request.
 *
 * The DMA API's Cognito authorizer rejects an unusable token before any
 * Lambda runs, with a plain `{"message":"Unauthorized"}` body -- the "other
 * body" branch above, which keeps the pre-migration retry-or-end-session
 * decision unchanged. Its identity middleware runs after, so a request that
 * reaches it already carries a token the gateway accepted: `missing` and
 * `malformed` mean that token's claims cannot be resolved to an account, a
 * problem a refreshed token will not fix, so that branch skips the retry.
 */
async function apiFetch<T>(
  api: EndPointTypes,
  controller?: AbortController
): Promise<{ status: number; data: T }> {
  const params: any = api.params || {}; // eslint-disable-line
  const { headers } = apiConfig;
  const isDMA = api.defaultBaseURL === BaseURLSelect.DMA;
  const baseURL = isDMA ? dmaBaseURL : palmettoBaseURL;
  const isProtected = api.authorization === AuthProtect.REQUIRED;

  /**
   * The credential this request sends. The DMA API's Cognito authorizer accepts
   * only the raw ID token; the Palmetto API accepts only the opaque access
   * token. Selecting by target API rather than by `AuthProtect.REQUIRED` alone
   * keeps a future endpoint from inheriting the wrong credential just by
   * declaring itself protected.
   *
   * `freshToken` is whatever `ensureFreshSession()` just returned. The ID token
   * is read from the store rather than passed in because a refresh writes both
   * halves of the session from one token (`setRefreshedSession`), so the JWT
   * read here always belongs to the same session as `freshToken`.
   *
   *   happy:          DMA call, store holds a JWT -> raw Authorization header
   *   nil/null:       DMA call, jwtToken null -> null; `send` warns and the
   *                     request goes out unauthenticated, so the 401 is
   *                     traceable to the empty credential instead of guessed at
   *   empty/zero:     jwtToken '' -> same as null, both are falsy here
   *   upstream error: `refreshSession()` logs and returns null, and
   *                     `ensureFreshSession()` falls back to the stored token --
   *                     unchanged from before this migration
   */
  const readCredential = (freshToken: Nullable<string>): Nullable<string> =>
    isDMA ? store.getState().authToken.jwtToken : freshToken;

  const send = (credential: Nullable<string>): Promise<AxiosResponse<T>> => {
    // The Cognito authorizer reads the ID token off the header and ignores the
    // query string entirely, so a DMA request carries no `access_token` at all.
    if (credential && !isDMA) {
      params.access_token = credential;
    }
    const apiConfigMerge: AxiosRequestConfig = merge.all([
      apiConfig,
      api,
      {
        baseURL,
        headers,
        params,
        timeout: REQUEST_TIMEOUT_MS,
      },
    ]);
    const axiosCreate = axios.create(apiConfigMerge);
    axiosCreate.interceptors.request.use(
      async function (config) {
        if (controller) {
          config.signal = controller.signal;
        }
        if (credential) {
          // Raw, with no `Bearer` prefix -- both APIs read the header verbatim.
          config.headers.Authorization = credential;
        } else if (isProtected && isDMA) {
          console.warn(missingJwtWarning);
        }
        return config;
      },
      function (error) {
        console.log('🚀apiFetch function ERROR: ', redactCredentials(error));
        throw Error(error);
      }
    );
    return axiosCreate(api);
  };

  // Renew a near-expired session before the request leaves, so an expiring
  // token produces a silent refresh instead of a 401 the caller has to survive.
  const credential = isProtected ? readCredential(await ensureFreshSession()) : null;

  // A request that never reached the server (offline, DNS, abort) carries no
  // response body, so it is reported as a generic technical issue rather than a
  // server error the caller could act on.
  const toClientError = (error: unknown): Error => {
    // Logged ahead of the branch so a failure that did reach the server still
    // shows its status and body; the credential is stripped on either path.
    console.log('🚀apiFetch ERROR: ', redactCredentials(error));
    const response = isAxiosError(error) ? error.response : undefined;
    if (response) return Error(JSON.stringify(response.data));
    return Error(genericErrorMessage);
  };

  try {
    const res = await send(credential);
    return { status: res.status, data: res.data };
  } catch (error: unknown) {
    const axiosError = isAxiosError(error) ? error : undefined;
    const status = axiosError?.response?.status;
    // Scoped to the DMA API deliberately. `auth.identity.*` is that API's own
    // middleware contract; the Palmetto API answers 401 for authorization
    // denials as well as dead sessions, so reading a `code` off its body could
    // end a session over a single forbidden request -- the exact harm the
    // comment above this function warns about.
    const identityCode =
      isProtected && isDMA ? readIdentityFailureCode(axiosError?.response?.data) : null;
    const isIdentityReauthFailure =
      identityCode === AUTH_IDENTITY_MISSING || identityCode === AUTH_IDENTITY_MALFORMED;

    if (identityCode) {
      // Recognized either way, so it is diagnosable by its own code instead of
      // vanishing into toClientError's generic log line below -- `nobody`
      // still falls through to that generic disposition, just logged first.
      console.warn(`apiFetch: ${identityCode} on ${axiosError?.config?.url}`);
    }

    if (isProtected && isIdentityReauthFailure) {
      // The gateway already accepted this token, so a broken claim will not be
      // fixed by retrying. An offline device is protected the same way a
      // captive-portal 401 is below; isSessionUnrecoverable() also folds in a
      // token-expiry check that does not apply here, so this reads the same
      // connection signal directly instead of reusing that function.
      if (selectConnectionHealth(store.getState())) {
        endSession();
        throw new AuthFailureError();
      }
      // Offline: the session is kept and the request falls through to the
      // generic error below, so a captive portal cannot end a field session.
      // Note this branch is hard to reach in practice -- an identity code
      // requires a response body, which requires the request to have reached
      // the server -- so it is a guard against a mid-flight transition rather
      // than the common offline case.
    } else if (status === 401 && isProtected) {
      if (await isSessionUnrecoverable()) {
        // Logged here because the throw below bypasses toClientError, which is
        // where every other failure in this catch gets its console record.
        console.warn(`apiFetch: session unrecoverable after 401 on ${axiosError?.config?.url}`);
        endSession();
        throw new AuthFailureError();
      } else {
        // The session was renewable, so the 401 came from a token that expired
        // between the pre-flight check and the response. Retry once with the
        // refreshed credential; a second 401 is an authorization denial and
        // belongs to the caller, not to the session.
        //
        // The comparison is against the credential this request actually sent.
        // A renewal issues a new ID token while the opaque Palmetto token stays
        // the same value, so comparing the opaque token would skip the retry on
        // exactly the DMA calls where it would have worked.
        const refreshedCredential = readCredential(await ensureFreshSession());
        if (refreshedCredential && refreshedCredential !== credential) {
          try {
            const retry = await send(refreshedCredential);
            return { status: retry.status, data: retry.data };
          } catch (retryError: unknown) {
            throw toClientError(retryError);
          }
        }
      }
    }

    throw toClientError(error);
  }
}

export default apiFetch;
